import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { createPowerReservation, updatePowerReservation } from '../../../api/iot';
import { errorMessages } from '../../../utils/methods';

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '448px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '0 24px',
  backgroundColor: '#262626',
});

const ModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
});

const CloseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '& svg': {
    color: '#fff',
    fontSize: '24px',
  },
});

const ModalContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  backgroundColor: '#fafbfc',
});

const FormCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  backgroundColor: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  width: '400px',
});

const FormSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'flex-start',
  justifyContent: 'center',
  width: '100%',
  borderRadius: '8px',
});

const FormLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const LabelText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
});

const LabelTextMedium = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  whiteSpace: 'nowrap',
});

const StyledRadioGroup = styled(RadioGroup)({
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  height: '32px',
  alignItems: 'center',
});

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  gap: '8px',
  '& .MuiFormControlLabel-label': {
    marginLeft: 0,
  },
});

const StyledRadio = styled(Radio)({
  padding: 0,
  '&.Mui-checked': {
    color: '#4ab3bc',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
});

const RadioLabelText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
});

// 조명 선택 박스
const LightSelectionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '12px',
  border: '1px solid #a6a6a6',
  borderRadius: '8px',
  width: '100%',
  marginTop: '8px',
});

const LightSelectionLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
});

const LightSelectionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  gap: '12px',
  width: '100%',
});

const LightCheckboxItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StyledCheckbox = styled(Checkbox)({
  padding: 0,
  '&.Mui-checked': {
    color: '#4ab3bc',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
});

const LightCheckboxLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '4px',
    '& fieldset': {
      borderColor: '#a6a6a6',
    },
    '&:hover fieldset': {
      borderColor: '#a6a6a6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4ab3bc',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    padding: '12px 16px',
    '&::placeholder': {
      color: '#a6a6a6',
      opacity: 1,
    },
  },
});

const DateTimeRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  width: '100%',
});

const DateInputWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const TimeInputGroup = styled(Box)({
  flex: 1,
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

const StyledSelect = styled(Select)({
  flex: 1,
  height: '44px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a6a6a6',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a6a6a6',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4ab3bc',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px !important',
    paddingRight: '16px !important',
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
  },
  '& .MuiSelect-icon': {
    display: 'none',
  },
});

const TimeUnit = styled(Typography)({
  fontSize: '13px',
  color: '#737373',
  whiteSpace: 'nowrap',
  marginLeft: '8px',
});

// 시간/분 옵션
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const DurationRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const DurationInputGroup = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  width: '178px',
});

const DurationHint = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
});

// 유지시간 동안 무슨 일이 일어나는지 (매니저앱 예약 등록과 같은 문구)
const DurationNotice = styled(Typography)({
  marginTop: '10px',
  padding: '10px 12px',
  borderRadius: '8px',
  backgroundColor: '#F2FAFA',
  border: '1px solid #BFE3E6',
  fontSize: '13px',
  lineHeight: 1.5,
  color: '#0F7F85',
  '& b': { fontWeight: 700 },
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

const CancelButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  padding: '12px 32px',
  backgroundColor: '#d9d9d9',
  borderRadius: '8px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#c9c9c9',
  },
});

const CancelButtonText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
});

const SubmitButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '200px',
  height: '40px',
  padding: '12px 24px',
  backgroundColor: disabled ? '#ccc' : '#4ab3bc',
  borderRadius: '8px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  pointerEvents: disabled ? 'none' : 'auto',
  '&:hover': {
    backgroundColor: disabled ? '#ccc' : '#3da3ac',
  },
}));

const SubmitButtonText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
});

// ============================================
// Main Component
// ============================================
const DoorTimeSettingModal = ({ open, onClose, onSaved, initialData, branchId, sensorId, token, relayData = [] }) => {
  const { t } = useTranslation();
  const toast = useToast();

  // relayData에서 조명 채널 목록 추출
  const availableLights = relayData?.map(r => r.relayChannel).sort((a, b) => a - b) || [];

  const getDefaultFormData = () => ({
    linkage: 'linked',
    selectedLights: availableLights,
    reservationName: '',
    openDate: dayjs().format('YYYY-MM-DD'),
    openHour: dayjs().format('HH'),
    openMinute: dayjs().format('mm'),
    durationHour: '00',
    durationMinute: '00',
  });

  const [formData, setFormData] = useState(getDefaultFormData());

  const isEditMode = !!initialData;

  // 예약 등록/수정 mutation — 수정 모드는 PUT (기존엔 수정도 신규 등록이라 예약이 중복됐음)
  const reservationMutation = useMutation({
    mutationFn: data =>
      isEditMode
        ? updatePowerReservation(initialData.id, data, token)
        : createPowerReservation({ reservations: [data] }, token),
    onSuccess: () => {
      toast.success(isEditMode ? t('예약이 수정되었습니다') : t('예약이 등록되었습니다'));
      onClose();
      onSaved?.();
    },
    onError: error => {
      console.error('예약 저장 에러:', error);
      toast.error(errorMessages(error) || (isEditMode ? t('예약 수정에 실패했습니다') : t('예약 등록에 실패했습니다')));
    },
  });

  // relayData 변경 시 selectedLights 업데이트
  useEffect(() => {
    if (relayData?.length > 0 && !initialData) {
      const lights = relayData.map(r => r.relayChannel).sort((a, b) => a - b);
      setFormData(prev => ({ ...prev, selectedLights: lights }));
    }
  }, [relayData]);

  // initialData가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (initialData) {
      setFormData({
        linkage: initialData.linkage || 'linked',
        selectedLights: initialData.selectedLights || availableLights,
        reservationName: initialData.reservationName || '',
        openDate: initialData.openDate || dayjs().format('YYYY-MM-DD'),
        openHour: initialData.openTime?.split(':')[0] || '00',
        openMinute: initialData.openTime?.split(':')[1] || '00',
        durationHour: String(initialData.durationHour || 0).padStart(2, '0'),
        durationMinute: String(initialData.durationMinute || 0).padStart(2, '0'),
      });
    } else {
      setFormData(getDefaultFormData());
    }
  }, [initialData, open]);

  // 조명 토글 핸들러
  const handleLightToggle = lightNum => {
    setFormData(prev => {
      const isSelected = prev.selectedLights.includes(lightNum);
      const newLights = isSelected
        ? prev.selectedLights.filter(l => l !== lightNum)
        : [...prev.selectedLights, lightNum];
      return { ...prev, selectedLights: newLights };
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // 유효성 검사
    if (!formData.reservationName.trim()) {
      toast.error(t('예약명을 입력해주세요'));
      return;
    }

    if (!sensorId) {
      toast.error(t('센서 정보가 없습니다'));
      return;
    }

    // 개방유지시간 계산 (분 단위)
    const periodTime = String(parseInt(formData.durationHour) * 60 + parseInt(formData.durationMinute));

    // executeAt 생성 (YYYY-MM-DD HH:mm:ss 형식)
    const executeAt = `${formData.openDate} ${formData.openHour}:${formData.openMinute}:00`;

    // 연동이면 전원 제어(전체/개별), 미연동이면 도어만
    const isLinked = formData.linkage === 'linked';
    if (isLinked && formData.selectedLights.length === 0) {
      toast.error(t('연동할 전원을 선택해주세요'));
      return;
    }
    const isAllLights =
      formData.selectedLights.length === availableLights.length &&
      availableLights.every(light => formData.selectedLights.includes(light));

    // API 요청 데이터 생성 (수정 모드는 branchId/sensorId 없이 PUT)
    const requestData = {
      reservationName: formData.reservationName,
      ...(isEditMode ? {} : { branchId, sensorId }),
      controlType: !isLinked ? null : isAllLights ? 'LIGHT_ALL' : 'LIGHT',
      relayChannels: !isLinked || isAllLights ? null : [...formData.selectedLights].sort((a, b) => a - b).join(','),
      onOff: isLinked ? 'on' : null,
      doorOpenYn: 'Y',
      executeAt: executeAt,
      periodTime: periodTime,
    };

    reservationMutation.mutate(requestData);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{isEditMode ? t('예약 수정') : t('예약 등록')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <FormCard>
            {/* 예약명 */}
            <FormSection>
              <LabelText>{t('예약명')}</LabelText>
              <StyledTextField
                placeholder={t('예약명을 입력해 주세요.')}
                value={formData.reservationName}
                onChange={e => handleChange('reservationName', e.target.value)}
              />
            </FormSection>

            {/* 개방일시 */}
            <FormSection>
              <LabelTextMedium>{t('실행일시')}</LabelTextMedium>
              <DateTimeRow>
                <DateInputWrapper>
                  <StyledTextField
                    type="date"
                    value={formData.openDate}
                    onChange={e => handleChange('openDate', e.target.value)}
                    InputProps={{
                      style: { height: '44px' },
                    }}
                  />
                </DateInputWrapper>
                <TimeInputGroup>
                  <StyledSelect
                    value={formData.openHour}
                    onChange={e => handleChange('openHour', e.target.value)}
                    renderValue={value => (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{value}</span>
                        <TimeUnit>{t('시')}</TimeUnit>
                      </Box>
                    )}>
                    {HOURS.map(h => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <StyledSelect
                    value={formData.openMinute}
                    onChange={e => handleChange('openMinute', e.target.value)}
                    renderValue={value => (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{value}</span>
                        <TimeUnit>{t('분')}</TimeUnit>
                      </Box>
                    )}>
                    {MINUTES.map(m => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </TimeInputGroup>
              </DateTimeRow>
            </FormSection>

            {/* 예약 유지시간 — 지나면 전원 OFF 자식 예약 실행 + 출입문 자동잠김 */}
            <FormSection>
              <FormLabel>
                <LabelTextMedium>{t('예약 유지시간')}</LabelTextMedium>
              </FormLabel>
              <DurationRow>
                <DurationInputGroup>
                  <StyledSelect
                    value={formData.durationHour}
                    onChange={e => handleChange('durationHour', e.target.value)}
                    renderValue={value => (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{value}</span>
                        <TimeUnit>{t('시간')}</TimeUnit>
                      </Box>
                    )}>
                    {HOURS.map(h => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <StyledSelect
                    value={formData.durationMinute}
                    onChange={e => handleChange('durationMinute', e.target.value)}
                    renderValue={value => (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{value}</span>
                        <TimeUnit>{t('분')}</TimeUnit>
                      </Box>
                    )}>
                    {MINUTES.map(m => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </DurationInputGroup>
                <DurationHint>{t('동안 유지')}</DurationHint>
              </DurationRow>
              <DurationNotice>
                {t('실행일시부터 유지시간 동안')} <b>{t('전원이 켜지고 출입문도 열려 있습니다.')}</b>
                <br />
                {t('유지시간이 끝나면')} <b>{t('전원 전체(조명·냉난방·전광판)가 꺼지고')}</b> {t('출입문은 자동잠김 됩니다.')}
              </DurationNotice>
            </FormSection>

            {/* 조명 · 냉난방 연동 */}
            <FormSection>
              <FormLabel>
                <LabelText>{t('조명 · 냉난방 연동')}</LabelText>
              </FormLabel>
              <StyledRadioGroup value={formData.linkage} onChange={e => handleChange('linkage', e.target.value)}>
                <StyledFormControlLabel
                  value="linked"
                  control={<StyledRadio />}
                  label={<RadioLabelText>{t('연동')}</RadioLabelText>}
                />
                <StyledFormControlLabel
                  value="unlinked"
                  control={<StyledRadio />}
                  label={<RadioLabelText>{t('미연동')}</RadioLabelText>}
                />
              </StyledRadioGroup>
              {/* 연동 선택 시 조명 선택 박스 표시 */}
              {formData.linkage === 'linked' && (
                <LightSelectionBox>
                  <LightSelectionLabel>{t('연동항목')}</LightSelectionLabel>
                  <LightSelectionRow>
                    {availableLights.map(lightNum => {
                      const relayInfo = relayData?.find(r => r.relayChannel === lightNum);
                      const lightName = relayInfo?.relayName || `${t('조명')} ${String(lightNum).padStart(2, '0')}`;
                      return (
                        <LightCheckboxItem key={lightNum}>
                          <StyledCheckbox
                            checked={formData.selectedLights.includes(lightNum)}
                            onChange={() => handleLightToggle(lightNum)}
                          />
                          <LightCheckboxLabel>{lightName}</LightCheckboxLabel>
                        </LightCheckboxItem>
                      );
                    })}
                  </LightSelectionRow>
                </LightSelectionBox>
              )}
            </FormSection>
          </FormCard>

          {/* 버튼 */}
          <ButtonGroup>
            <CancelButton onClick={onClose}>
              <CancelButtonText>{t('취소')}</CancelButtonText>
            </CancelButton>
            <SubmitButton disabled={reservationMutation.isPending} onClick={handleSubmit}>
              <SubmitButtonText>
                {reservationMutation.isPending ? t('처리중...') : isEditMode ? t('수정하기') : t('예약하기')}
              </SubmitButtonText>
            </SubmitButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default DoorTimeSettingModal;
